import type { ApiClient } from "@albertine/api-client";
import {
    type CurrencyCode,
    type ListAllPaymentsData,
    type ListAllPaymentsSortField,
    type PaymentMethod,
    type PaymentStatus,
    SuccessResponseListAllPaymentsSchema,
} from "@albertine/types/openapi";
import * as Sentry from "@sentry/react";

export type SortOrder = "asc" | "desc";

export type PaymentsListFilter = {
    sortBy: ListAllPaymentsSortField;
    sortOrder: SortOrder;
    currency: CurrencyCode[];
    paymentMethod: PaymentMethod[];
    createdBy: string[];
    status: PaymentStatus[];
};

export function formatPaymentMethodLabel(paymentMethod: string): string {
    if (paymentMethod === "payment-link") {
        return "Payment link";
    }
    if (paymentMethod === "other") {
        return "Other";
    }
    if (
        paymentMethod === "card" ||
        paymentMethod.startsWith("pm_") ||
        paymentMethod.startsWith("src_") ||
        paymentMethod.startsWith("card_")
    ) {
        return "Saved card";
    }
    return paymentMethod;
}

export function formatPaymentStatusLabel(status: PaymentStatus): string {
    switch (status) {
        case "PAYMENT_REQUESTED":
            return "Payment requested";
        case "DRAFT_INVOICE_CREATED":
            return "Draft invoice created";
        case "INVOICE_FINALIZED":
            return "Invoice finalized";
        case "INVOICE_PAID":
            return "Invoice paid";
        default: {
            const _exhaustive: never = status;
            return _exhaustive;
        }
    }
}

export function formatPaymentAmount(amount: number): string {
    return new Intl.NumberFormat("en-GB", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    }).format(amount);
}

export async function queryListAllPaymentsApi(
    apiClient: ApiClient,
    filter: PaymentsListFilter,
    pageOffsetZeroBased: number,
    pageSize: number,
): Promise<ListAllPaymentsData> {
    const pageIndexOneBased = Math.floor(pageOffsetZeroBased / pageSize) + 1;
    type ListAllPaymentsRequest = Parameters<ApiClient["get"]["listAllPayments"]>[0];

    try {
        const httpResponse = await apiClient.get.listAllPayments({
            query: {
                sortBy: filter.sortBy,
                sortOrder: filter.sortOrder,
                page: String(pageIndexOneBased),
                pageSize: String(pageSize),
                ...(filter.currency.length > 0 ? { currency: filter.currency } : {}),
                ...(filter.paymentMethod.length > 0
                    ? { paymentMethod: filter.paymentMethod }
                    : {}),
                ...(filter.createdBy.length > 0 ? { createdBy: filter.createdBy } : {}),
                ...(filter.status.length > 0 ? { status: filter.status } : {}),
            } as ListAllPaymentsRequest["query"],
        });
        const json: unknown = await httpResponse.json();
        const parsed = SuccessResponseListAllPaymentsSchema.safeParse(json);

        if (!parsed.success) {
            Sentry.captureException(parsed.error, {
                extra: {
                    message: "Failed to load payment requests (unexpected response)",
                },
            });
            return { total: 0, payments: [] };
        }

        return parsed.data.data;
    } catch (error) {
        Sentry.captureException(error, {
            extra: { message: "Failed to load payment requests" },
        });
        return { total: 0, payments: [] };
    }
}
